import { useEffect, useState } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { Package, Zap, Trash2, Plus, MapPin, Loader2, Gift, Copy, Truck, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../lib/api";
import { plur } from "../lib/localize";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { StatusBadge } from "./Admin";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

const EMPTY_ADDR = { label: "", address1: "", address2: "", city: "", postal_code: "", phone: "", default: false };

export default function Account() {
  const { user, setUser } = useAuth();
  const { lang, t, ts } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  // Retour à la prise de rendez-vous après avoir complété son compte
  // (demande du propriétaire, 2026-10-20 : « le ramener au calendrier »)
  const retour = location.state?.next || "";
  const retournerAuCalendrier = () => { if (retour) navigate(retour); };
  const [orders, setOrders] = useState(null);
  const [profile, setProfile] = useState(null);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [referrals, setReferrals] = useState(null);
  const [soumissions, setSoumissions] = useState(null);  // demandes de soumission de forfaits
  const [myQuotes, setMyQuotes] = useState(null);        // soumissions 30 s (trophées) du dossier

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then((res) => setOrders(res.data)).catch(() => setOrders([]));
      api.get("/account/profile").then((res) => setProfile(res.data)).catch(() => setProfile(null));
      api.get("/account/referrals").then((res) => setReferrals(res.data)).catch(() => setReferrals(null));
      api.get("/my/soumissions").then((res) => setSoumissions(res.data.soumissions || [])).catch(() => setSoumissions([]));
      api.get("/account/quotes").then((res) => setMyQuotes(res.data.quotes || [])).catch(() => setMyQuotes([]));
    }
  }, [user]);

  // Étape la plus avancée d'une soumission 30 s (même workflow que l'onglet admin)
  const quoteStep = (qq) =>
    qq.livre ? ["st_livre", "text-emerald-400 border-emerald-400/30"]
    : qq.expedie ? ["st_expedie", "text-sky-300 border-sky-400/30"]
    : qq.commande ? ["st_commande", "text-gold border-gold/40"]
    : qq.paye ? ["st_paye", "text-emerald-300 border-emerald-400/30"]
    : qq.accepte ? ["st_accepte", "text-ivory/70 border-ivory/25"]
    : ["st_attente", "text-amber-400 border-amber-400/30"];

  const referralLink = referrals?.code ? `${window.location.origin}/inscription?ref=${referrals.code}` : "";
  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success(t("referral.copied"));
    } catch {
      toast.error(referralLink);
    }
  };

  if (user === false) return <Navigate to="/connexion" state={{ next: "/compte" }} replace />;
  if (!user) return null;

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));
  const setA = (k) => (e) => setAddrForm((a) => ({ ...a, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/account/profile", {
        first_name: profile.first_name, last_name: profile.last_name,
        phone: profile.phone, email: profile.email,
      });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(t("account_extra.profile_saved"));
      retournerAuCalendrier();  // retour au calendrier de rendez-vous si on en vient
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/account/addresses", addrForm);
      setProfile((p) => ({ ...p, addresses: data }));
      setAddrForm(EMPTY_ADDR);
      setShowAddrForm(false);
      toast.success(t("account_extra.address_added"));
      retournerAuCalendrier();  // retour au calendrier de rendez-vous si on en vient
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setDefaultAddress = async (id) => {
    try {
      const { data } = await api.post(`/account/addresses/${id}/default`);
      setProfile((p) => ({ ...p, addresses: data }));
      toast.success(t("account_extra.default_saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteAddress = async (id) => {
    try {
      const { data } = await api.delete(`/account/addresses/${id}`);
      setProfile((p) => ({ ...p, addresses: data }));
      toast.success(t("account_extra.address_deleted"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/compte" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("account.hello", { name: user.name })}</p>
        <h1 className="font-serif text-5xl md:text-6xl mb-12">{t("account.title")}</h1>
      </FadeUp>

      {/* Venu de la page rendez-vous pour compléter son compte : bandeau + retour */}
      {retour && (
        <div className="mb-8 rounded-xl border border-gold/50 bg-gold/10 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="account-retour-rdv">
          <p className="text-sm text-gold">{t("rdv.complete_profile")}</p>
          <button type="button" onClick={retournerAuCalendrier} data-testid="account-retour-btn"
            className="rounded-full bg-gold text-ink text-xs font-bold px-5 py-2.5 hover:bg-champagne transition-colors">
            ← {t("rdv.kicker")}
          </button>
        </div>
      )}

      {profile && (
        <div className="grid md:grid-cols-2 gap-6 mb-14">
          <FadeUp delay={0.05}>
            <form onSubmit={saveProfile} className="bg-surface border border-ivory/10 rounded-xl p-7 h-full" data-testid="profile-form">
              <h2 className="font-serif text-2xl mb-6">{t("account_extra.profile_title")}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-ivory/70 mb-1.5">{t("account_extra.first_name")}</label>
                    <input data-testid="profile-first-name" required value={profile.first_name} onChange={setP("first_name")} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm text-ivory/70 mb-1.5">{t("account_extra.last_name")}</label>
                    <input data-testid="profile-last-name" required value={profile.last_name} onChange={setP("last_name")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">{t("account_extra.phone")}</label>
                  <input data-testid="profile-phone" type="tel" value={profile.phone} onChange={setP("phone")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">{t("account_extra.email")}</label>
                  <input data-testid="profile-email" type="email" required value={profile.email} onChange={setP("email")} className={inputCls} />
                </div>
                <button data-testid="profile-save" type="submit" disabled={savingProfile}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                  {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />} {t("account_extra.save")}
                </button>
              </div>
            </form>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 h-full" data-testid="addresses-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl">{t("account_extra.addresses_title")}</h2>
                <button data-testid="address-add-toggle" onClick={() => setShowAddrForm((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Plus className="w-3.5 h-3.5" /> {t("account_extra.add_address")}
                </button>
              </div>
              {profile.addresses.length === 0 && !showAddrForm && (
                <p className="text-sm text-ivory/65" data-testid="no-addresses">{t("account_extra.no_addresses")}</p>
              )}
              <div className="space-y-3">
                {profile.addresses.map((a) => (
                  <div key={a.id} data-testid={`address-${a.id}`} className="flex items-start gap-3 border border-ivory/10 rounded-lg p-4">
                    <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm">
                      {a.label && <p className="text-gold text-xs uppercase tracking-widest mb-1">{a.label}</p>}
                      {a.default && (
                        <p className="text-[10px] uppercase tracking-widest text-ink bg-gold inline-block rounded-full px-2 py-0.5 mb-1"
                          data-testid={`address-default-badge-${a.id}`}>{t("account_extra.default_badge")}</p>
                      )}
                      <p>{a.address1}{a.address2 ? `, ${a.address2}` : ""}</p>
                      <p className="text-ivory/60">{a.city} {a.postal_code}</p>
                      {a.phone && <p className="text-ivory/65 text-xs mt-1">{a.phone}</p>}
                    </div>
                    <button data-testid={`address-default-${a.id}`} onClick={() => setDefaultAddress(a.id)}
                      disabled={!!a.default}
                      className={`transition-colors duration-300 shrink-0 ${a.default ? "text-gold" : "text-ivory/65 hover:text-gold"}`}
                      title={t("account_extra.set_default")}>
                      <Star className="w-4 h-4" fill={a.default ? "currentColor" : "none"} />
                    </button>
                    <button data-testid={`address-delete-${a.id}`} onClick={() => deleteAddress(a.id)}
                      className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0" title={ts("account_extra.address_deleted")}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {showAddrForm && (
                <form onSubmit={addAddress} className="space-y-3 mt-4 border-t border-ivory/10 pt-4" data-testid="address-form">
                  <input data-testid="address-label" value={addrForm.label} onChange={setA("label")} placeholder={ts("account_extra.address_label")} className={inputCls} />
                  <input data-testid="address-address1" required value={addrForm.address1} onChange={setA("address1")} placeholder={ts("checkout.address1")} className={inputCls} />
                  <input data-testid="address-address2" value={addrForm.address2} onChange={setA("address2")} placeholder={ts("checkout.address2")} className={inputCls} />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="address-city" required value={addrForm.city} onChange={setA("city")} placeholder={ts("checkout.city")} className={inputCls} />
                    <input data-testid="address-postal" required value={addrForm.postal_code} onChange={setA("postal_code")} placeholder={ts("checkout.postal_code")} className={inputCls} />
                  </div>
                  <input data-testid="address-phone" type="tel" value={addrForm.phone} onChange={setA("phone")} placeholder={ts("account_extra.phone")} className={inputCls} />
                  <label className="flex items-center gap-2 text-sm text-ivory/75">
                    <input type="checkbox" data-testid="address-default-check" checked={!!addrForm.default}
                      onChange={(e) => setAddrForm((f) => ({ ...f, default: e.target.checked }))}
                      className="accent-gold w-4 h-4" />
                    {t("account_extra.set_default")}
                  </label>
                  <button data-testid="address-save" type="submit"
                    className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300">
                    {t("account_extra.save")}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      )}

      {referrals && referrals.code && (
        <FadeUp delay={0.12} className="mb-14">
          <div className="bg-surface border border-gold/20 rounded-xl p-7" data-testid="referral-card">
            <div className="flex items-center gap-3 mb-3">
              <Gift className="w-5 h-5 text-gold" />
              <h2 className="font-serif text-2xl">{t("referral.title")}</h2>
            </div>
            <p className="text-sm text-ivory/60 mb-5 max-w-2xl">{t("referral.text")}</p>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <code data-testid="referral-link" className="flex-1 min-w-64 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-champagne truncate">{referralLink}</code>
              <button data-testid="referral-copy" onClick={copyReferral}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                <Copy className="w-4 h-4" /> {t("referral.copy")}
              </button>
            </div>
            <div className="flex flex-wrap gap-6 text-sm mb-6">
              <p data-testid="referral-clicks" className="text-ivory/60">{t("referral.clicks")} : <strong className="text-gold">{referrals.clicks}</strong></p>
              <p data-testid="referral-count" className="text-ivory/60">{t("referral.referred")} : <strong className="text-gold">{referrals.referred_count}</strong></p>
              <Link to="/parrainage" data-testid="referral-page-link-btn" className="text-gold hover:text-champagne underline underline-offset-2 transition-colors duration-300">
                {t("footer.referral")} →
              </Link>
            </div>
            <h3 className="text-xs uppercase tracking-widest text-ivory/65 mb-3">{t("referral.rewards")}</h3>
            {referrals.rewards.length === 0 ? (
              <p className="text-sm text-ivory/65" data-testid="referral-no-rewards">{t("referral.no_rewards")}</p>
            ) : (
              <div className="space-y-2">
                {referrals.rewards.map((r) => (
                  <p key={r.id} data-testid={`referral-reward-${r.code}`} className="text-sm text-ivory/70">
                    <strong className="text-gold tracking-wider">{r.code}</strong>
                    {" — "}{r.kind === "pct" ? `−${r.value} %` : `−${r.value} $`}
                    {" · "}{t("referral.valid_until")} {r.valid_to}
                    {r.uses > 0 && <span className="text-ivory/65"> · {t("referral.used")}</span>}
                  </p>
                ))}
              </div>
            )}
          </div>
        </FadeUp>
      )}

      {/* Mes soumissions de forfaits tournoi (suivi des demandes) */}
      {soumissions !== null && soumissions.length > 0 && (
        <FadeUp delay={0.05}>
          <div className="bg-surface border border-gold/20 rounded-xl p-6 mb-8" data-testid="my-soumissions">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-xs uppercase tracking-widest text-gold">{t("mySoumissions.title")}</h3>
              <Link to="/forfaits" data-testid="my-soumissions-new"
                className="text-xs text-gold hover:underline underline-offset-2">
                {t("mySoumissions.new")} →
              </Link>
            </div>
            <ul className="space-y-2.5">
              {soumissions.map((s) => (
                <li key={s.id} data-testid={`my-soumission-${s.id}`}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-ivory/5 pb-2.5 last:border-0 last:pb-0">
                  <span className="font-semibold text-ivory/90 capitalize">{s.package_label || s.package}</span>
                  <span className="text-ivory/60">{s.players} {t("mySoumissions.players")}</span>
                  {s.estimated_total != null && (
                    <span className="text-gold">{t("mySoumissions.estimated")} : {fmt(s.estimated_total)}</span>
                  )}
                  <span className="text-ivory/45 text-xs">
                    {t("mySoumissions.sent_on")} {new Date(s.created_at).toLocaleDateString("fr-CA", { timeZone: "America/Montreal" })}
                  </span>
                  <span className={`ml-auto text-[10px] uppercase tracking-wider rounded-full px-2.5 py-0.5 border ${
                    s.done ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}
                    data-testid={`my-soumission-status-${s.id}`}>
                    {s.done ? t("mySoumissions.done") : t("mySoumissions.pending")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>
      )}

      {/* Mes soumissions 30 s (trophées) — la trace de la commande dans le dossier,
          rattachée au compte au moment du paiement (ou par courriel identique) */}
      {myQuotes !== null && myQuotes.length > 0 && (
        <FadeUp delay={0.06}>
          <div className="bg-surface border border-gold/20 rounded-xl p-6 mb-8" data-testid="my-quotes">
            <h3 className="text-xs uppercase tracking-widest text-gold mb-4">{t("myQuotes.title")}</h3>
            <ul className="space-y-2.5">
              {myQuotes.map((qq) => {
                const [stepKey, stepCls] = quoteStep(qq);
                return (
                  <li key={qq.id} data-testid={`my-quote-${qq.id}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-ivory/5 pb-2.5 last:border-0 last:pb-0">
                    <span className="text-ivory/90 font-semibold">{qq.summary}</span>
                    <span className="text-ivory/60">
                      {lang === "en"
                        ? `${qq.items_count} ${qq.items_count === 1 ? "item" : "items"}`
                        : plur(qq.items_count, "article")}
                    </span>
                    {qq.amount != null && <span className="text-gold">{fmt(qq.amount)}</span>}
                    <span className="text-ivory/45 text-xs">
                      {t("myQuotes.sent_on")} {new Date(qq.created_at).toLocaleDateString("fr-CA", { timeZone: "America/Montreal" })}
                    </span>
                    <span className={`ml-auto text-[10px] uppercase tracking-wider rounded-full px-2.5 py-0.5 border ${stepCls}`}
                      data-testid={`my-quote-status-${qq.id}`}>
                      {t(`myQuotes.${stepKey}`)}
                    </span>
                    {qq.epreuve_acceptee_le && (
                      <span className="text-[10px] text-emerald-300" data-testid={`my-quote-proof-${qq.id}`}>
                        {t("myQuotes.proof_accepted")} {new Date(qq.epreuve_acceptee_le).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { timeZone: "America/Montreal" })}
                        {qq.epreuve_avertissement ? ` — ${t("myQuotes.proof_warning")}` : ""}
                      </span>
                    )}
                    {qq.portal && (
                      <Link to={qq.portal} data-testid={`my-quote-portal-${qq.id}`}
                        className="text-xs text-gold hover:underline underline-offset-2">
                        {t("myQuotes.portal")} →
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </FadeUp>
      )}

      {orders === null ? (
        <p className="text-ivory/70">{t("account.loading")}</p>
      ) : orders.length === 0 ? (
        <FadeUp delay={0.1} className="text-center py-20 border border-dashed border-ivory/15 rounded-xl">
          <Package className="w-10 h-10 text-gold/50 mx-auto mb-4" />
          <p className="text-ivory/70 mb-6" data-testid="no-orders">{t("account.none")}</p>
          <Link to="/boutique" className="inline-flex bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
            {t("account.browse")}
          </Link>
        </FadeUp>
      ) : (
        <div className="space-y-6">
          {orders.map((o, idx) => (
            <FadeUp key={o.order_number} delay={idx * 0.05}>
              <div data-testid={`order-${o.order_number}`} className="bg-surface border border-ivory/10 rounded-xl p-6">
                <div className="flex flex-wrap justify-between gap-4 mb-4">
                  <div>
                    <p className="font-serif text-2xl">{o.order_number}</p>
                    <p className="text-ivory/65 text-xs mt-1">
                      {new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { timeZone: "America/Montreal", year: "numeric", month: "long", day: "numeric" })}
                      {" · "}{t("account.delivery")} : {o.customer.delivery_date}
                    </p>
                  </div>
                  <StatusBadge status={o.status} label={t(`status.${o.status}`)} />
                  {o.express && (
                    <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-gold/60 text-gold bg-gold/10" data-testid={`express-badge-${o.order_number}`}>
                      <Zap className="w-3 h-3" /> {t("express.badge")}
                    </span>
                  )}
                </div>
                {o.tracking_number && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
                    <p className="text-xs text-gold" data-testid={`tracking-${o.order_number}`}>
                      {t("account_extra.tracking")} :{" "}
                      {o.tracking_url ? (
                        <a href={o.tracking_url} target="_blank" rel="noreferrer"
                          data-testid={`tracking-link-${o.order_number}`}
                          className="font-bold underline underline-offset-4 decoration-gold/50 hover:text-champagne transition-colors duration-300">
                          {o.tracking_number}
                        </a>
                      ) : (
                        <strong>{o.tracking_number}</strong>
                      )}
                    </p>
                    {/* Statut du colis EN DIRECT (Stallion, robot 3 h) — demande du propriétaire */}
                    {o.ship_status && (
                      <span data-testid={`ship-status-${o.order_number}`}
                        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider rounded-full px-3 py-1 border ${
                          o.ship_status.toLowerCase() === "delivered" ? "border-green-500/50 text-green-400 bg-green-500/10"
                          : o.ship_status.toLowerCase() === "exception" ? "border-red-400/50 text-red-300 bg-red-400/10"
                          : "border-gold/50 text-gold bg-gold/10"}`}>
                        <Truck className="w-3 h-3" />
                        {t(`shipstatus.${o.ship_status.toLowerCase()}`) !== `shipstatus.${o.ship_status.toLowerCase()}` ? t(`shipstatus.${o.ship_status.toLowerCase()}`) : o.ship_status}
                        {o.ship_status_at && ` — ${new Date(o.ship_status_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { timeZone: "America/Montreal", month: "short", day: "numeric" })}`}
                      </span>
                    )}
                  </div>
                )}
                <div className="space-y-2 border-t border-ivory/10 pt-4">
                  {o.items.map((i, k) => (
                    <div key={k} className="flex justify-between text-sm gap-4">
                      <p className="text-ivory/70">
                        {i.name} <span className="text-ivory/65">× {i.qty}</span>
                        <span className="text-ivory/65"> — {i.size_label}, {i.inscription_label}</span>
                        {i.inscription_text && <span className="text-champagne italic"> « {i.inscription_text} »</span>}
                        {i.logo_name && <span className="text-ivory/65"> · {t("cart.logo", { name: i.logo_name })}</span>}
                      </p>
                      <p className="text-champagne whitespace-nowrap">{fmt(i.line_total)}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-ivory/10 mt-4 pt-3 text-right">
                  {o.promo && (
                    <p className="text-sm text-gold mb-1" data-testid={`account-promo-${o.order_number}`}>
                      {t("promo.discount")} ({o.promo.code}) : −{fmt(o.promo.discount)}
                    </p>
                  )}
                  <p className="font-serif text-xl text-gold">{t("account.total")} : {fmt(o.total)}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      )}
    </div>
  );
}
